import type { LocalStore } from './local-store.js';

/**
 * Browser-native persistent LocalStore backed by IndexedDB (the web edition of
 * the SQLite-first local-first store). One object store holds all records,
 * keyed by `${collection}\u0000${id}`, so per-collection listing is a bounded
 * key-range cursor. UI never touches this directly — only repositories do.
 *
 * Strict-local data still never syncs: that is enforced by the mutation queue
 * + privacy class, independent of where bytes are persisted.
 */
const DB_NAME = 'life-os';
const STORE = 'kv';
const SEP = '\u0000';

function keyFor(collection: string, id: string): string {
  return `${collection}${SEP}${id}`;
}

export class IndexedDbLocalStore implements LocalStore {
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(private readonly dbName: string = DB_NAME) {}

  private open(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available in this environment'));
        return;
      }
      const req = indexedDB.open(this.dbName, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    });
    return this.dbPromise;
  }

  private async tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
    const db = await this.open();
    return db.transaction(STORE, mode).objectStore(STORE);
  }

  async get<T>(collection: string, id: string): Promise<T | undefined> {
    const store = await this.tx('readonly');
    return new Promise<T | undefined>((resolve, reject) => {
      const req = store.get(keyFor(collection, id));
      req.onsuccess = () => resolve((req.result as T) ?? undefined);
      req.onerror = () => reject(req.error);
    });
  }

  async put<T>(collection: string, id: string, value: T): Promise<void> {
    const store = await this.tx('readwrite');
    return new Promise<void>((resolve, reject) => {
      const req = store.put(value, keyFor(collection, id));
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async list<T>(collection: string): Promise<readonly T[]> {
    const store = await this.tx('readonly');
    const range = IDBKeyRange.bound(`${collection}${SEP}`, `${collection}${SEP}\uffff`);
    return new Promise<readonly T[]>((resolve, reject) => {
      const out: T[] = [];
      const req = store.openCursor(range);
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          out.push(cursor.value as T);
          cursor.continue();
        } else {
          resolve(out);
        }
      };
      req.onerror = () => reject(req.error);
    });
  }

  async count(collection: string): Promise<number> {
    const store = await this.tx('readonly');
    const range = IDBKeyRange.bound(`${collection}${SEP}`, `${collection}${SEP}\uffff`);
    return new Promise<number>((resolve, reject) => {
      const req = store.count(range);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  async entries<T>(collection: string): Promise<readonly (readonly [string, T])[]> {
    const store = await this.tx('readonly');
    const prefix = `${collection}${SEP}`;
    const range = IDBKeyRange.bound(prefix, `${prefix}\uffff`);
    return new Promise<readonly (readonly [string, T])[]>((resolve, reject) => {
      const out: (readonly [string, T])[] = [];
      const req = store.openCursor(range);
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          const fullKey = String(cursor.key);
          const id = fullKey.startsWith(prefix) ? fullKey.slice(prefix.length) : fullKey;
          out.push([id, cursor.value as T]);
          cursor.continue();
        } else {
          resolve(out);
        }
      };
      req.onerror = () => reject(req.error);
    });
  }
}
